import {
  PassPurchaseTransactionDataInput,
  PaymentMethod,
  useCreatePassPurchaseMutation,
  useCreatePassTransactionMutation,
} from 'drycut-graphql';
import { Button } from 'ui-components';
import { useEffect, useState } from 'react';
import DatatransErrorModal from './DatatransErrorModal';
import Lightbox from 'react-datatrans-light-box';
import { useNavigate } from 'react-router-dom';
import { UseFormReturn } from 'react-hook-form';
import { env } from 'navigation';
import { StylistPassConfirmation } from '../StylistPassConfirmation';

type Props = {
  data: PassPurchaseTransactionDataInput;
  amount: number;
  formMethods: UseFormReturn<{ name: string; phone: string }>;
};

export function PassPayment({ formMethods, amount, data }: Props): JSX.Element {
  const [createPayment, { loading: creatingPayment }] = useCreatePassTransactionMutation();
  const [createPurchase, { loading: creatingPurchase }] = useCreatePassPurchaseMutation();
  const [showStylistModal, setShowStylistModal] = useState(false);
  const [stylistConfirmed, setStylistConfirmed] = useState(false);
  const [invalidCode, setInvalidCode] = useState(false);
  const [transactionError, setTransactionError] = useState<string>();
  const [datatransId, setDatatransId] = useState<string>();
  const navigate = useNavigate();

  useEffect(() => {
    // In manual, if the stylist has confirmed the pass, we can proceed with the payment
    if (stylistConfirmed) {
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stylistConfirmed]);

  const handleSubmit = async () => {
    const validationResult = await formMethods.trigger();

    if (!validationResult) {
      return;
    }

    // If we are in manual mode, we need the stylist to confirm the pass
    if (data.manual && !stylistConfirmed) {
      setShowStylistModal(true);
      return;
    }

    let paymentMethod: PaymentMethod = PaymentMethod.Unk;

    if (data.manual) {
      paymentMethod = PaymentMethod.Direct;
    }

    // A coupon code was used so the amount to pay is 0, we should not show the Datatrans lightbox
    if (amount === 0) {
      paymentMethod = PaymentMethod.Skip;
    }

    // If it's a manual pass purchase or the amount to pay is 0, we skip the payment step
    const skipPayment =
      paymentMethod === PaymentMethod.Skip || paymentMethod === PaymentMethod.Direct;

    const response = await createPayment({
      variables: { input: { amount, paymentMethod, data } },
    });

    const payload = response.data?.createPassTransaction;

    if (payload?.errors && payload?.errors?.length > 0) {
      setTransactionError(payload.errors[0].message);
    } else {
      setTransactionError('');
      const transaction = payload?.paymentTransaction;

      // If it's a skip payment, we can create the booking right away and redirect the user to the
      // pass details. Otherwise the redirect will be a hard one from the backend, after Datatrans
      // has done its thing.

      if (payload && skipPayment && transaction?.id) {
        const booking = await createPurchase({
          variables: { input: { paymentTransactionId: transaction?.id } },
        });

        const basePath = data.storeSlug ? `/${data.storeSlug}` : '';
        const uid = booking.data?.createPassPurchase?.passPurchase?.uid;

        navigate(`${basePath}/pass/${uid}`);
      }

      if (transaction?.datatransId) {
        setDatatransId(transaction.datatransId);
      }
    }
  };

  const handleStylistConfirm = (code: string) => {
    // The code is the current date in the format YYMMDD
    const getCurrentDate = () => {
      const today = new Date();
      const year = today.getFullYear().toString().slice(-2);
      const month = (today.getMonth() + 1).toString().padStart(2, '0');
      const day = today.getDate().toString().padStart(2, '0');
      return `${year}${month}${day}`;
    };

    const validCode = getCurrentDate();

    // Verify the code
    if (code !== validCode) {
      setInvalidCode(true);
      return;
    }

    setStylistConfirmed(true);
    setShowStylistModal(false);
  };

  return (
    <>
      <Button
        type="button"
        data-mode={env}
        loading={
          formMethods.formState.isValidating || creatingPayment || creatingPurchase || !!datatransId
        }
        onClick={handleSubmit}
      >
        {data.manual ? 'Buchen' : 'Bezahlen'}
      </Button>

      {datatransId && (
        <Lightbox
          transactionId={datatransId}
          production={env === 'production'}
          onCancelled={() => setDatatransId('')}
        />
      )}

      <DatatransErrorModal
        open={!!transactionError}
        error={transactionError || ''}
        onClose={() => setTransactionError('')}
      />

      <StylistPassConfirmation
        open={showStylistModal}
        onClose={() => setShowStylistModal(false)}
        onConfirm={handleStylistConfirm}
        invalidCode={invalidCode}
      />
    </>
  );
}
